import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryOptions,
  UseMutationOptions,
} from 'react-query'
import {
  AssignUserSiteRole,
  DeleteUserSiteRole,
  ListUserSiteRolesResponse,
  ResponseError,
  UsersApi,
} from '@trybeapp/sdk'
import { useUser } from 'hooks/UseUser'
import { apiConfiguration } from 'utilities/TrybeApiClient'

const users = new UsersApi(apiConfiguration)

export const useUserSiteRoles = (
  userId: string,
  options: UseQueryOptions<ListUserSiteRolesResponse, ResponseError> = {}
) => {
  return useQuery<ListUserSiteRolesResponse, ResponseError>(
    ['userSiteRoles', userId],
    () => users.listUserSiteRoles({ userId }),
    options
  )
}

export const useAssignSiteRoleToUser = (
  options: UseMutationOptions<void, ResponseError, [string, AssignUserSiteRole]> = {}
) => {
  const queryClient = useQueryClient()

  return useMutation<void, ResponseError, [string, AssignUserSiteRole]>(
    ([userId, assignSiteRole]) =>
      users.actionAssignUserSiteRole({ userId, assignUserSiteRole: assignSiteRole }),
    {
      ...options,
      onSuccess: () => {
        queryClient.invalidateQueries('userSiteRoles')
      },
    }
  )
}

export const useRemoveSiteRoleToUser = (
  options: UseMutationOptions<void, ResponseError, [string, DeleteUserSiteRole]> = {}
) => {
  const queryClient = useQueryClient()

  return useMutation<void, ResponseError, [string, DeleteUserSiteRole]>(
    ([userId, removeSiteRole]) =>
      users.deleteUserSiteRole({ userId, deleteUserSiteRole: removeSiteRole }),
    {
      ...options,
      onSuccess: () => {
        queryClient.invalidateQueries('userSiteRoles')
      },
    }
  )
}

export const useCurrentUserSitePermissions = () => {
  const { sub: userId } = useUser()

  return useQuery<ListUserSiteRolesResponse, ResponseError>(
    ['currentUserSiteRoles'],
    () => users.listUserSiteRoles({ userId }),
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      cacheTime: Infinity,
      staleTime: Infinity,
    }
  )
}
